/**
 * CtxParamGate — layout-route gate that switches context before rendering
 * children when the URL carries `?ctx=org:<id>` or `?ctx=network:<id>` (#404).
 *
 * Write-safety hold (#404 slice 1c): waits for in-flight saves (not a
 * notification's mark-read, Q-506 card 1571) and active uploads to settle before
 * issuing the PUT. The hold is a pure-TS subscription
 * (`holdUntilWritesSettle` in ctxGateHold.ts) stored in a ref. ONE effect keyed
 * on `handledKey` drives the entire flow; cleanup on key change or unmount
 * cancels the hold and resets a still-waiting phase to idle.
 *
 * Critical render invariant: when a well-formed ctx param is detected, the gate
 * blocks <Outlet> (renders LoadingState) synchronously during the render body,
 * BEFORE the effect fires. This prevents ProtectedRoute from running against the
 * old context and bouncing to a default route.
 */

import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { parseCtxParam, stripCtxParam } from '../../ctxParam';
import { useCtxGateStore } from '../../ctxGateStore';
import { holdUntilWritesSettle, countBlockingMutations, type HoldHandle } from '../../ctxGateHold';
import { useSwitchContext } from '../../hooks/useContextSwitching';
import { updateCurrentUser, readSeed, getCurrentUser } from '@/auth/authData';
import { getDefaultRoute } from '@/lib/defaultRoute';
import { useUploadQueueStore } from '@/modules/upload-queue/store/useUploadQueueStore';
import { ACTIVE_STATUSES } from '@/modules/upload-queue/constants';
import { LoadingState } from '@/components/ui/LoadingState/LoadingState';
import { ContextNoAccess } from '../ContextNoAccess';
import { CTX_GATE_HOLD_MAX_MS } from '@/constants/ctxGateMessages';
import { reportClientError } from '@/api/clientErrors';
import type { ContextSwitchInput } from '@/types';

/**
 * #1829 (F3): true when `target` is the context the session is already in. A
 * missing or unknown current context is never "the same": the gate then takes
 * the PUT path as before.
 */
function isCurrentContext(target: ContextSwitchInput): boolean {
  const ctx = getCurrentUser()?.context;
  if (!ctx || ctx.type !== target.type) return false;
  const currentId = target.type === 'org' ? ctx.organization?.id : ctx.network?.id;
  return currentId === target.id;
}

function getErrorStatus(error: unknown): number | null {
  if (axios.isAxiosError(error)) {
    return error.response?.status ?? null;
  }
  return null;
}

export function CtxParamGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const phase = useCtxGateStore((s) => s.phase);
  const setPhase = useCtxGateStore((s) => s.setPhase);

  const switchCtx = useSwitchContext({ navigateAfter: false, pageOwnsFailure: true });

  // Stable refs — startSwitch reads these, never closure captures.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const switchCtxRef = useRef(switchCtx);
  switchCtxRef.current = switchCtx;

  const parsedRef = useRef<ContextSwitchInput | null>(null);
  const switchLocationRef = useRef<{ pathname: string; search: string; hash: string } | null>(null);
  const statusRef = useRef<number | null>(null);
  const holdRef = useRef<HoldHandle | null>(null);

  const parsed = parseCtxParam(location.search);
  // #1829 (F3): a ctx equal to the current context needs no PUT, hold or cache
  // invalidation; the param is only stripped (see the strip effect below).
  const alreadyThere = parsed !== null && isCurrentContext(parsed);

  // Capture refs every render.
  if (parsed) {
    parsedRef.current = parsed;
    switchLocationRef.current = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };
  }

  // handledKey: non-null when a well-formed ctx is present. The single effect
  // is keyed on this. When it changes (new ctx, or ctx removed), the effect
  // cleanup fires, cancelling any active hold.
  const handledKey = parsed && !alreadyThere ? location.search : null;

  // One startSwitch() used by hold-release, immediate path, and retry.
  function startSwitch() {
    const input = parsedRef.current;
    if (!input) return;
    useCtxGateStore.getState().setPhase('switching');

    void (async () => {
      try {
        const result = await switchCtxRef.current.mutateAsync(input);
        updateCurrentUser(result);
        void navigateRef.current(stripCtxParam(switchLocationRef.current!), { replace: true });
        useCtxGateStore.getState().setPhase('idle');
      } catch (error: unknown) {
        void queryClientRef.current.invalidateQueries({ queryKey: ['auth', 'me'] });
        statusRef.current = getErrorStatus(error);
        useCtxGateStore.getState().setPhase('refused');
      }
    })();
  }

  // --- Single hold-and-switch effect, keyed only on handledKey ---
  useEffect(() => {
    if (handledKey === null) return;

    let cancelled = false;

    async function run() {
      const qc = queryClientRef.current;

      const uploadsActive = useUploadQueueStore.getState().items.some(
        (item) => (ACTIVE_STATUSES as readonly string[]).includes(item.status),
      );
      if (countBlockingMutations(qc) > 0 || uploadsActive) {
        useCtxGateStore.getState().setPhase('waiting');
        const hold = holdUntilWritesSettle({
          queryClient: qc,
          maxMs: CTX_GATE_HOLD_MAX_MS,
          onTimeout: () => {
            reportClientError({
              source: 'context',
              name: 'ctx-gate-hold-timeout',
              error: new Error('ctx gate hold timed out; proceeding'),
              message: 'ctx gate hold timed out; proceeding',
              context: { holdMaxMs: CTX_GATE_HOLD_MAX_MS },
            });
          },
        });
        holdRef.current = hold;
        const holdResult = await hold.promise;
        holdRef.current = null;
        if (cancelled) return;
        if (holdResult !== 'settled' && holdResult !== 'timeout') return;
      }

      if (cancelled) return;
      startSwitch();
    }

    void run();

    return () => {
      cancelled = true;
      if (holdRef.current) {
        holdRef.current.cancel();
        holdRef.current = null;
      }
      // Reset a still-waiting phase to idle on key change or unmount.
      if (useCtxGateStore.getState().phase === 'waiting') {
        useCtxGateStore.getState().setPhase('idle');
      }
    };
  }, [handledKey]);

  // Handle malformed ctx: strip without request.
  useEffect(() => {
    const raw = new URLSearchParams(location.search).get('ctx');
    if (raw && !parsed) {
      // stripCtxParam preserves search by construction; scanner recognises it
      void navigate(stripCtxParam(location), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // #1829 (F3): ctx = the context we are already in: strip it, keep the rest.
  useEffect(() => {
    if (parsed && alreadyThere && phase === 'idle') {
      void navigate(stripCtxParam(location), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, alreadyThere, phase]);

  const handleRetry = () => {
    startSwitch();
  };

  const handleGoHome = async () => {
    await queryClient.refetchQueries({ queryKey: ['auth', 'me'] });
    const user = readSeed();
    const route = user ? getDefaultRoute(user) : '/';
    // keep-search-exempt: ctx-refused-go-home
    void navigate({ pathname: route, search: '', hash: '' }, { replace: true });
    setPhase('idle');
  };

  // Block <Outlet> when a ctx URL is detected but the effect hasn't completed
  // yet (phase is still idle). This prevents ProtectedRoute from running against
  // the old context. The effect will set phase to 'waiting' or 'switching'.
  // 'waiting' (a genuine save or upload in flight) looks the same as 'switching'.
  if ((handledKey !== null && phase === 'idle') || phase === 'waiting' || phase === 'switching') {
    return <SwitchingWait onRetry={phase === 'switching' ? startSwitch : undefined} />;
  }

  if (phase === 'refused') {
    const kind = parsedRef.current?.type ?? 'org';
    return (
      <ContextNoAccess
        kind={kind}
        status={statusRef.current}
        onRetry={handleRetry}
        onGoHome={() => void handleGoHome()}
      />
    );
  }

  return <Outlet />;
}

/**
 * The switching wait: the spinner alone, and the quiet "Try again" under it
 * after 15 s (Q-506 card pattern-2 (Q-509 a), #1561); it re-issues the switch.
 * The 15 s clock is the shared one in LoadingState: it survives the remount
 * between the hold and the switch and restarts when Try again is clicked.
 */
function SwitchingWait({ onRetry }: { onRetry: (() => void) | undefined }) {
  return <LoadingState shape="unknown" onRetry={onRetry} />;
}
